import { DialogComponent } from '@theme/dialog';
import { buildCrossShopUrl } from '@theme/geo-ip-target';

/**
 * Country and language switcher dialog. Extends the theme dialog with:
 * - a country list that swaps in over the main view (Escape steps back to it)
 * - one language radio group per market, swapped to match the picked country
 * - a single Apply: native localization form submit (country + language) for
 *   markets on this shop, a redirect to the language URL for other shops
 * - `aria-expanded` state on every trigger, including ones outside the element
 *
 * @typedef {object} CountrySwitcherRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} [trigger]
 * @property {HTMLElement} mainView
 * @property {HTMLElement} listView
 * @property {HTMLElement} listTitle
 * @property {HTMLButtonElement} countryButton
 * @property {HTMLElement} countryFlag
 * @property {HTMLElement} countryName
 * @property {HTMLInputElement} countryInput
 * @property {HTMLFieldSetElement} languages
 *
 * @typedef {object} Selection
 * @property {HTMLElement | null} entry - The country list entry, if the country is listed.
 * @property {string} country - ISO country code.
 * @property {string | undefined} market - Key of the market's language group.
 * @property {string} language - Locale to check, or '' for the market's primary one.
 * @property {Node | null} flag
 * @property {string} name
 *
 * @extends DialogComponent
 */
class CountrySwitcherComponent extends DialogComponent {
  /**
   * The selection the page was rendered with. Closing the dialog without Apply
   * returns to it.
   *
   * @type {Selection}
   */
  #initial = { entry: null, country: '', market: undefined, language: '', flag: null, name: '' };

  /**
   * The list entry picked last. Apply reads it directly rather than looking the
   * country code up again, so it always acts on the entry the visitor clicked.
   *
   * @type {HTMLElement | null}
   */
  #selectedEntry = null;

  connectedCallback() {
    super.connectedCallback();

    const { countryInput, countryFlag, countryName, languages, listView } = this.refs;
    const currentEntry = listView?.querySelector('[aria-current="true"]');

    this.#initial = {
      entry: currentEntry instanceof HTMLElement ? currentEntry : null,
      country: countryInput?.value ?? '',
      market: languages?.querySelector('[data-market]:not([hidden])')?.getAttribute('data-market') ?? undefined,
      language: this.#checkedLanguage()?.value ?? '',
      flag: countryFlag?.firstElementChild?.cloneNode(true) ?? null,
      name: countryName?.textContent ?? '',
    };
    this.#selectedEntry = this.#initial.entry;

    this.addEventListener('dialog:open', this.#syncTriggerState);
    this.addEventListener('dialog:close', this.#syncTriggerState);
    this.addEventListener('dialog:close', this.#restoreInitial);
    this.addEventListener('submit', this.#onSubmit);
    // Capture phase: the base dialog closes itself on Escape from a bubble-phase
    // listener on this same element, so this has to run first to intercept it.
    this.addEventListener('keydown', this.#onEscape, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('dialog:open', this.#syncTriggerState);
    this.removeEventListener('dialog:close', this.#syncTriggerState);
    this.removeEventListener('dialog:close', this.#restoreInitial);
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('keydown', this.#onEscape, { capture: true });
    document.removeEventListener('keyup', this.#swallowEscapeKeyup, { capture: true });
  }

  /**
   * Swaps the main view for the country list and moves focus to the picked
   * country, so keyboard users continue from where they are.
   */
  showCountryList = () => {
    const { mainView, listView, listTitle, countryButton } = this.refs;
    if (!mainView || !listView) return;

    mainView.hidden = true;
    listView.hidden = false;
    countryButton?.setAttribute('aria-expanded', 'true');

    const current = listView.querySelector('[aria-current="true"]');
    if (current instanceof HTMLElement) {
      current.focus();
      current.scrollIntoView({ block: 'center' });
    } else {
      listTitle?.focus();
    }
  };

  /**
   * Picks a country from the list and returns to the main view. A country in
   * the same market keeps the picked language (same list of languages);
   * re-picking the page's own country restores its language; any other country
   * starts on its market's primary language.
   *
   * @param {Event} event
   */
  selectCountry = (event) => {
    const entry = /** @type {HTMLElement} */ (event.target).closest?.('[data-country]');
    if (!(entry instanceof HTMLElement)) return;

    const country = entry.dataset.country ?? '';
    const flag = entry.querySelector('.country-flag')?.cloneNode(true) ?? null;
    const name = entry.querySelector('.country-switcher__country-name')?.textContent ?? '';
    const activeMarket = this.refs.languages?.querySelector('[data-market]:not([hidden])')?.getAttribute('data-market');

    let language = '';
    if (entry.dataset.market === activeMarket) {
      language = this.#checkedLanguage()?.value ?? '';
    } else if (entry === this.#initial.entry) {
      language = this.#initial.language;
    }

    this.#setSelection({ entry, country, market: entry.dataset.market, language, flag, name });
    this.#showMainView();
    this.refs.countryButton?.focus();
  };

  /**
   * While the country list is open, Escape returns to the main view. A second
   * Escape then closes the dialog.
   *
   * @param {KeyboardEvent} event
   */
  #onEscape = (event) => {
    if (event.key !== 'Escape' || !this.refs.dialog?.open) return;

    // The base dialog closes on keydown. Opened from the mobile drawer, focus is
    // then back on the drawer trigger, and Horizon's header drawer closes itself
    // on the keyup that follows. Swallow that keyup so Escape closes one layer.
    document.addEventListener('keyup', this.#swallowEscapeKeyup, { capture: true });

    if (!this.refs.listView || this.refs.listView.hidden) return;

    event.preventDefault();
    event.stopPropagation();
    this.#showMainView();
    this.refs.countryButton?.focus();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #swallowEscapeKeyup = (event) => {
    if (event.key !== 'Escape') return;

    event.stopPropagation();
    document.removeEventListener('keyup', this.#swallowEscapeKeyup, { capture: true });
  };

  /**
   * Apply. Same-shop markets fall through to the native localization form,
   * which switches country and language in one submit and keeps the cart and
   * consent cookies. Other shops get a redirect to the chosen language's URL.
   *
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    const { countryInput } = this.refs;
    if (!countryInput || event.target !== countryInput.form) return;

    const entry = this.#selectedEntry;
    const language = this.#checkedLanguage();

    // Compare against the page's actual language, not the pre-checked radio:
    // when the market's language list lacks the current language, the primary
    // one is pre-checked, and applying it is a real switch.
    const languageUnchanged = !language || language.value.toLowerCase() === this.dataset.language;

    if (entry === this.#initial.entry && languageUnchanged) {
      event.preventDefault();
      this.closeDialog();
      return;
    }

    // No entry: this shop's own market is not in the config, and only its own
    // languages are on offer — a native switch as well.
    if (!entry) return;

    // Markets without language data carry no radios; their domain is the
    // market default language.
    const base = language?.dataset.url || entry.dataset.base;

    // A market still on its old site (external in the GCE payload) is not a
    // Shopify storefront: it has none of our paths, and the query parameters
    // mean nothing to it. Deep-linking there lands on its 404 instead of the
    // market the visitor asked for, so send them to the root.
    //
    // This has to be decided before the native branch below. An external market
    // can sit in this very shop — Austria, Czechia, Slovakia, Greece, Poland and
    // the UK are all markets of the EU shop — and carries `data-native` too. The
    // native form would then switch the market inside the shop and keep the
    // visitor here, which is the opposite of what picking that country means.
    if (entry.hasAttribute('data-external')) {
      if (!base) return;
      event.preventDefault();
      window.location.assign(base);
      return;
    }

    if (entry.hasAttribute('data-native')) return;

    event.preventDefault();
    if (!base) return;

    // Keeps path, query and hash below the target's locale root. `?country=`
    // pins the picked country in multi-country markets (EU), which would
    // otherwise resolve via the target shop's own cookie or geo-IP; `?cs=1`
    // makes the target's 404 page fall back to its homepage.
    const { pathname, search, hash } = window.location;
    const root = window.Shopify?.routes?.root ?? '/';
    const country = entry.dataset.country ?? '';

    window.location.assign(buildCrossShopUrl(base, { root, pathname, search, hash, country }));
  };

  /**
   * Discards a pending selection when the dialog closes without Apply.
   */
  #restoreInitial = () => {
    this.#showMainView();

    const { flag } = this.#initial;
    this.#setSelection({ ...this.#initial, flag: flag?.cloneNode(true) ?? null });
  };

  /**
   * @param {Selection} selection
   */
  #setSelection({ entry, country, market, language, flag, name }) {
    const { countryInput, countryFlag, countryName, languages, listView } = this.refs;

    this.#selectedEntry = entry;
    if (countryInput) countryInput.value = country;
    if (countryFlag && flag) countryFlag.replaceChildren(flag);
    if (countryName) countryName.textContent = name;

    for (const item of listView?.querySelectorAll('[data-country]') ?? []) {
      if (item === entry) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    }

    if (!languages) return;

    /** @type {HTMLElement | null} */
    let activeGroup = null;

    // Disabled radios are left out of the form submit, so only the visible
    // market's language can ever reach Shopify.
    for (const group of languages.querySelectorAll('[data-market]')) {
      if (!(group instanceof HTMLElement)) continue;

      const isActive = market !== undefined && group.dataset.market === market;
      group.hidden = !isActive;
      for (const input of group.querySelectorAll('input')) input.disabled = !isActive;
      if (isActive) activeGroup = group;
    }

    languages.hidden = !activeGroup;
    if (!activeGroup) return;

    const inputs = [...activeGroup.querySelectorAll('input')];
    const match = inputs.find((input) => input.value === language) ?? inputs[0];
    if (match) match.checked = true;
  }

  #showMainView() {
    const { mainView, listView, countryButton } = this.refs;

    if (listView) listView.hidden = true;
    if (mainView) mainView.hidden = false;
    countryButton?.setAttribute('aria-expanded', 'false');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #checkedLanguage() {
    const input = this.refs.languages?.querySelector('input:checked:not(:disabled)');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Mirrors the dialog's open state onto `aria-expanded` of every trigger that
   * controls it — the header's desktop and drawer triggers live outside this
   * element.
   */
  #syncTriggerState = () => {
    const { dialog } = this.refs;
    if (!dialog?.id) return;

    for (const trigger of document.querySelectorAll(`[aria-controls="${CSS.escape(dialog.id)}"]`)) {
      trigger.setAttribute('aria-expanded', String(dialog.open));
    }
  };
}

if (!customElements.get('country-switcher-component')) {
  customElements.define('country-switcher-component', CountrySwitcherComponent);
}
